import { getBrandName, getLoginUrl } from './appSettings';

const SEND_EMAIL_URL =
  'https://nxvochdgdtnhbjbdckkw.supabase.co/functions/v1/send-email';

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Email de bienvenue / identifiants (création de compte).
 * Charte alignée sur la page de connexion (marketing teal / cyan).
 */
export async function sendCredentialsEmail({ email, password, fullName = '' }) {
  const to = String(email || '').trim().toLowerCase();
  if (!to) {
    throw new Error('Adresse email manquante pour l’envoi');
  }
  if (!password) {
    throw new Error('Mot de passe manquant pour l’envoi');
  }

  const brandName = await getBrandName();
  const loginUrl = await getLoginUrl();
  const displayName = fullName?.trim() || to;
  const safeName = escapeHtml(displayName);
  const safeEmail = escapeHtml(to);
  const safePassword = escapeHtml(password);
  const safeBrand = escapeHtml(brandName);
  const safeLoginUrl = escapeHtml(loginUrl);

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Identifiants de connexion ${safeBrand}</title>
      <style>
        body {
          font-family: Arial, Helvetica, sans-serif;
          line-height: 1.6;
          color: #0f2735;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background: #f3f7f8;
        }
        .header {
          background: linear-gradient(135deg, #0F766E 0%, #0891B2 100%);
          color: #E6F6F8;
          padding: 16px 24px;
          text-align: center;
          border-radius: 14px 14px 0 0;
          min-height: 12px;
        }
        .content {
          background: #ffffff;
          padding: 28px 24px;
          border-radius: 0 0 14px 14px;
          border: 1px solid rgba(125, 211, 252, 0.35);
          border-top: none;
        }
        .content p { margin: 0 0 14px; color: #0f2735; }
        .muted { color: #5b7380; }
        .credentials {
          background: #f0fafa;
          border: 1px solid rgba(15, 118, 110, 0.22);
          border-radius: 10px;
          padding: 18px;
          margin: 18px 0;
        }
        .credentials p { margin: 8px 0; }
        .credentials strong { color: #0F766E; }
        .password {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 15px;
          background: #e6f6f8;
          padding: 4px 8px;
          border-radius: 4px;
          color: #0A2533;
        }
        .button {
          display: inline-block;
          background: linear-gradient(135deg, #0F766E 0%, #0891B2 100%);
          color: #ffffff !important;
          text-decoration: none;
          padding: 12px 24px;
          border-radius: 8px;
          margin-top: 8px;
          font-weight: 600;
        }
        .warning {
          background: #fffbeb;
          border-left: 4px solid #f59e0b;
          padding: 12px;
          margin: 18px 0;
          font-size: 14px;
          color: #78350f;
        }
        .footer {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #6b7280;
          text-align: justify;
        }
        ul { padding-left: 20px; margin: 0 0 14px; color: #0f2735; }
        li { margin-bottom: 6px; }
      </style>
    </head>
    <body>
      <div class="header" aria-hidden="true"></div>
      <div class="content">
        <p>Bonjour ${safeName},</p>
        <p>Nous sommes ravis de vous accueillir sur notre plateforme <strong>${safeBrand}</strong>.</p>
        <p>Votre compte a été créé avec succès.</p>
        <p>Voici vos identifiants de connexion :</p>

        <div class="credentials">
          <p><strong>Email :</strong> ${safeEmail}</p>
          <p><strong>Mot de passe :</strong> <span class="password">${safePassword}</span></p>
        </div>

        <div class="warning">
          <strong>Important :</strong> pensez à changer votre mot de passe après votre première connexion.
        </div>

        <p>Pour vous connecter et suivre vos interventions :</p>
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 16px auto;">
          <tr>
            <td align="center" bgcolor="#0F766E" style="border-radius: 8px; background-color: #0F766E;">
              <a href="${safeLoginUrl}"
                 style="display: inline-block; padding: 12px 24px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: 600; color: #ffffff; text-decoration: none; border-radius: 8px; background-color: #0F766E;">
                Se connecter à mon compte
              </a>
            </td>
          </tr>
        </table>

        <p>Une fois connecté, vous pourrez :</p>
        <ul>
          <li>Suivre l’avancement de vos interventions</li>
          <li>Consulter votre historique de prestations</li>
          <li>Télécharger vos factures</li>
          <li>Gérer vos informations personnelles</li>
        </ul>

        <p class="muted">Si vous n’avez pas demandé la création de ce compte, vous pouvez ignorer cet email.</p>

        <div class="footer">
          <p>Cet email a été envoyé automatiquement, merci de ne pas y répondre.</p>
          <p>&copy; ${new Date().getFullYear()} ${safeBrand}. Tous droits réservés.</p>
        </div>
      </div>
    </body>
    </html>
  `;

  const response = await fetch(SEND_EMAIL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to,
      subject: `Identifiants de connexion ${brandName}`,
      html: htmlContent,
    }),
  });

  if (!response.ok) {
    throw new Error(`Erreur d’envoi de l’email (${response.status})`);
  }

  return true;
}

/** Variante non-bloquante (retourne false en cas d’échec). */
export async function sendCredentialsEmailSafe(params) {
  try {
    return await sendCredentialsEmail(params);
  } catch (error) {
    console.error("Erreur lors de l'envoi de l'email:", error);
    return false;
  }
}
